import { createAsyncThunk, createSlice, } from '@reduxjs/toolkit'
import { AppState } from '@app/store/state/app.state';
import moment, { Moment } from 'moment';
import {
  emptyWhereaboutsDay,
  Period,
  UsersWhereaboutsDay,
  WhereaboutsDayV2,
  WhereaboutsGroupType
} from '@features/whereabouts/models/movements.models';
import { WhereaboutsOption } from '@features/whereabouts/services/WhereaboutOptions';
import { requestUsersWeeklyMovements, updateWhereaboutsDay } from '@features/whereabouts/services/Movements';
import {
  updateCurrentUsersWhereabouts,
  updateTeamMembersWhereabouts
} from '@features/whereabouts/pages/team-movements/models/team-movements.duck';
import { DATE_FORMAT, getListOfDaysBetweenInclusiveToExclusive } from "@shared/utils/DateUtils";
import { DialogIdentifiers, openDialog, openDialogWithPayload } from "@shared/store/dialog.duck";
import { failureNotification, warningNotification } from "@shared/store/notification.duck";
import { WhereaboutsBookingErrorType } from "@features/desks/services/DeskBookingService";
import { WhereaboutsResponseError } from "@shared/models/api.models";
import { loadRecentDayNotes } from "./dayNotes.duck";
import { updateLineReportsWhereabouts } from "./LineReportsWhereabouts.duck";
import { updateFavouritedUsersWhereabouts } from "./FavouritedUsersWhereabouts.duck";
import { updateDailyMemberWhereabouts } from "./dailyWhereabouts.duck";
import { updateCompanyMemberWhereabouts } from "./companyMovements.duck";
import { ViewMode } from "@features/whereabouts/pages/whereabouts-v2/types";
import { setDailyFilter } from "./whereaboutsView.duck";
import { checkOfficeHasParkingEnabled } from "@shared/utils/OfficeHelper";
import { refreshWallPlannerActivities } from "@features/wall-planner/pages/wallPlanner.duck";

export type EditScope = 'day' | 'week';

export enum OfficeWhereaboutsView {
  Office, CarPark,
}

/**
 * Fans out a single saved whereabouts day to every Redux slice that may hold a
 * copy. Call after a successful PUT /api/v3/whereabouts; pass `isOwn` so the
 * `currentUsersWhereabouts` slice (which only ever holds the signed-in user)
 * is only updated when the saved row belongs to them.
 *
 * Each downstream reducer no-ops via .map() when the user isn't in its slice,
 * so blanket-dispatching is safe and cheap.
 */
export const dispatchWhereaboutsSaved: any = createAsyncThunk(
  'editMovements/dispatchWhereaboutsSaved',
  async (params: {whereabouts: UsersWhereaboutsDay, isOwn: boolean}, thunkAPI) => {
    const {whereabouts, isOwn} = params;
    if (isOwn) {
      thunkAPI.dispatch(updateCurrentUsersWhereabouts({whereaboutsToReplace: whereabouts}));
    }
    thunkAPI.dispatch(updateTeamMembersWhereabouts({whereaboutsToReplace: whereabouts}));
    thunkAPI.dispatch(updateLineReportsWhereabouts({whereaboutsToReplace: whereabouts}));
    thunkAPI.dispatch(updateFavouritedUsersWhereabouts({whereaboutsToReplace: whereabouts}));
    thunkAPI.dispatch(updateCompanyMemberWhereabouts({whereaboutsToReplace: whereabouts}));
    thunkAPI.dispatch(updateDailyMemberWhereabouts(whereabouts));
  }
)

export interface EditMovementsState {
  focusedUserId?: string;
  focusedGroupId?: WhereaboutsGroupType;
  activeDay?: Moment;
  activePeriod: Period;
  userRequiresParking: boolean;
  editingWhereabouts: UsersWhereaboutsDay[];
  editScope: EditScope;
}

export const initialEditMovementsState: EditMovementsState = {
  activeDay: moment(),
  activePeriod: Period.AllDay,
  userRequiresParking: false,
  editingWhereabouts: [],
  editScope: 'day',
}

export const startEditingWhereabouts: any = createAsyncThunk('editMovements/startEditingWhereabouts',
  async (params: {userId: string, date: Moment, groupId: WhereaboutsGroupType, scope?: EditScope}, thunkAPI) => {
    thunkAPI.dispatch(setDailyFilter(null));
    thunkAPI.dispatch(loadRecentDayNotes());
    const memberWhereabouts = await requestUsersWeeklyMovements(params.date, params.userId);
    const appState = (thunkAPI.getState() as AppState);

    const currentUser = appState.auth.currentUser;

    const weekCommencing = params.date.clone().startOf('isoWeek');

    const sevenDayWeek = appState.auth.currentUser?.companyEntity.sevenDayWhereaboutsWeekEnabled;
    const days = getListOfDaysBetweenInclusiveToExclusive(weekCommencing, weekCommencing.clone().add(sevenDayWeek ? 7 : 5, 'days'))

    const editingWhereabouts: UsersWhereaboutsDay[] = [];
    days.forEach(date => {
      const whereaboutsOnDate = (memberWhereabouts.whereabouts ?? []).find(mw => mw.date === date.format(DATE_FORMAT));
      if (whereaboutsOnDate) {
        editingWhereabouts.push(whereaboutsOnDate);
      } else {
        editingWhereabouts.push(emptyWhereaboutsDay(date.format(DATE_FORMAT), params.userId));
      }
    });

    return {
      userId: params.userId,
      groupId: params.groupId,
      date: params.date,
      period: Period.AllDay,
      editingWhereabouts: editingWhereabouts,
      userRequiresParking: currentUser?.id === params.userId && currentUser?.carParkingEnabled,
      scope: params.scope ?? 'day',
    }
  }
);

function handleWhereaboutsError(error: WhereaboutsResponseError, updatedWhereabouts: WhereaboutsDayV2, dispatch: any) {
  switch (error.type) {
    case WhereaboutsBookingErrorType.OUT_OF_BOOKING_RANGE:
      dispatch(openDialogWithPayload({
        payload: { weekCommencing: moment(updatedWhereabouts.date).format(DATE_FORMAT), officeId: updatedWhereabouts.amOfficeId || updatedWhereabouts.pmOfficeId },
        activeDialog: DialogIdentifiers.UnableToBookDesksErrorDialog
      }))
      break;
    case WhereaboutsBookingErrorType.CANNOT_BOOK_OVER_APPROVED_HOLIDAY:
      dispatch(openDialog(DialogIdentifiers.CannotEditApprovedHoliday));
      break;
    case WhereaboutsBookingErrorType.NO_PARKING_SPACE:
      dispatch(warningNotification('', `Parking unavailable on ${moment(updatedWhereabouts.date).format('dddd')}`));
      break;
    case WhereaboutsBookingErrorType.CANNOT_EDIT_PAST_WHEREABOUTS:
      dispatch(warningNotification('Cannot edit past whereabouts for that user', ''));
      break;
    case WhereaboutsBookingErrorType.DESK_NOT_ACTIVE:
      dispatch(warningNotification('That desk is not active', ''));
      break;
    default: dispatch(failureNotification('Contact support if the error persists', 'Failed to save'));
      break;
  }
}

export const endEditingWhereabouts: any = createAsyncThunk('editMovements/endEditingWhereabouts',
  async (params: void) => {
    return;
  }
);

function patchWhereaboutsDay(whereaboutsDay: UsersWhereaboutsDay, params: UpdateMovementParams, activePeriod: Period,
                             officeHasParkingEnabled: boolean, userRequiresParking: boolean, optionItemLabel?: string): any {
  let updatedWhereabouts: any = {...whereaboutsDay};
  if (activePeriod === Period.AM || activePeriod === Period.AllDay) {
    updatedWhereabouts = {
      ...updatedWhereabouts,
      amStatusId: params.selectedOption.id,
      amStatus: params.selectedOption.status,
      amOfficeId: params?.locationId ?? 0,
      amDeskId: params?.deskId ?? 0,
      amParkingSpaceId: params?.parkingSpaceId ?? 0,
      amDeskLabel: undefined,
      amGeneralParking: (officeHasParkingEnabled && userRequiresParking) && !params?.parkingSpaceId,
      isWhereaboutsSaving: false,
      whereaboutsUpdateFailed: false,
      dayNote: params.dayNote,
      amOptionItemId: params?.optionItemId,
      amOptionItemText: params?.optionItemText,
      amOptionItemLabel: optionItemLabel,
    }
  }
  if (activePeriod === Period.PM || activePeriod === Period.AllDay) {
    updatedWhereabouts = {
      ...updatedWhereabouts,
      pmStatusId: params.selectedOption.id,
      pmStatus: params.selectedOption.status,
      pmOfficeId: params?.locationId ?? 0,
      pmDeskId: params?.deskId ?? 0,
      pmParkingSpaceId: params?.parkingSpaceId ?? 0,
      pmDeskLabel: undefined,
      pmGeneralParking: (officeHasParkingEnabled && userRequiresParking) || !!params?.parkingSpaceId,
      isWhereaboutsSaving: false,
      whereaboutsUpdateFailed: false,
      dayNote: params.dayNote,
      pmOptionItemId: params?.optionItemId,
      pmOptionItemText: params?.optionItemText,
      pmOptionItemLabel: optionItemLabel,
    }
  }
  return updatedWhereabouts;
}

/**
 * PUTs one whereabouts day with the optimistic saving state and slice fan-out.
 * Returns the day as it should be held in state afterwards: the saved server
 * copy (plus optimistic option-item labels) on success, or the original rolled
 * back with whereaboutsUpdateFailed on error.
 */
async function saveWhereaboutsDay(updatedWhereabouts: any, originalWhereabouts: any, isOwn: boolean, dispatch: any,
                                  onError: (error: WhereaboutsResponseError) => void): Promise<any> {
  // Optimistically mark every slice as "saving" so the spinner stays
  // visible on the day cell even after the edit panel closes (the panel's
  // own editingWhereabouts state was the only place this flag lived).
  dispatch(dispatchWhereaboutsSaved({
    whereabouts: {...updatedWhereabouts, isWhereaboutsSaving: true},
    isOwn,
  }));

  await new Promise((resolve: any) => setTimeout(() => resolve(), 80));
  const result = await updateWhereaboutsDay(updatedWhereabouts);

  if (result.errors.length > 0) {
    onError(result.errors[0]);
    const rolledBack = {...originalWhereabouts, isWhereaboutsSaving: false, whereaboutsUpdateFailed: true};

    // Roll back the optimistic saving state across all slices.
    dispatch(dispatchWhereaboutsSaved({
      whereabouts: rolledBack,
      isOwn,
    }));
    return rolledBack;
  }

  // The saved entity carries option item ids/text but not display labels;
  // keep the labels from the optimistic copy so cells render immediately.
  const saved = {
    ...result.whereaboutsDay,
    amOptionItemLabel: updatedWhereabouts.amOptionItemLabel,
    pmOptionItemLabel: updatedWhereabouts.pmOptionItemLabel,
  };
  dispatch(dispatchWhereaboutsSaved({
    whereabouts: saved,
    isOwn,
  }));
  return saved;
}

interface UpdateMovementParams {
  selectedOption: WhereaboutsOption;
  locationId: number;
  activeDay?: string;
  activeUserId?: string;
  deskId?: number;
  skip?: boolean;
  stickOnDay?: boolean;
  parkingSpaceId?: number;
  dayNote?: string;
  optionItemId?: number;
  optionItemText?: string;
}

export const updateMovement: any = createAsyncThunk(
  'editMovements/updateMovement',
  async (params: UpdateMovementParams, thunkAPI) => {
    const state: AppState = thunkAPI.getState() as AppState;
    const editMovementsState = state.editMovements;
    const activeDay = params?.activeDay ?? editMovementsState.activeDay?.format(DATE_FORMAT);
    const activePeriod = editMovementsState.activePeriod;
    const officeHasParkingEnabled = checkOfficeHasParkingEnabled(params.locationId, state.advanceHotDeskingSetup.offices)

    // Optimistic display label for the picked option item; the projection provides
    // it on subsequent loads via am/pm_option_item_label.
    const optionItemLabel = params.optionItemText ??
      (params.optionItemId ? params.selectedOption.optionItems?.find(item => item.id === params.optionItemId)?.label : undefined);

    const currentUser = state.auth.currentUser;
    const isOwn = editMovementsState.focusedUserId === currentUser?.id;

    let updatedWhereabouts: any = undefined;
    let originalWhereabouts: any = undefined;
    editMovementsState.editingWhereabouts.forEach(whereaboutsDay => {
      if (whereaboutsDay.date === activeDay) {
        originalWhereabouts = {...whereaboutsDay};
        updatedWhereabouts = patchWhereaboutsDay(whereaboutsDay, params, activePeriod, officeHasParkingEnabled,
          editMovementsState.userRequiresParking, optionItemLabel);
      }
    })


    if (!params.stickOnDay) {
      const isDailyView = state.whereaboutsView.viewMode === ViewMode.Day;
      if (isDailyView) {
        if (activePeriod === Period.AM) {
          thunkAPI.dispatch(setActivePeriod(Period.PM));
        } else {
          thunkAPI.dispatch(endEditingWhereabouts());
          if (activePeriod === Period.PM) {
            thunkAPI.dispatch(setActivePeriod(Period.AllDay));
          }
        }
      } else if (activePeriod === Period.AllDay || activePeriod === Period.PM) {
        const currentDay = moment(activeDay).day();
        const sevenDayWhereaboutsWeekEnabled = state.auth.currentUser?.companyEntity.sevenDayWhereaboutsWeekEnabled
        if (sevenDayWhereaboutsWeekEnabled) {
          if (currentDay > 0 && currentDay < 7) {
            const updatedActiveDay = moment(activeDay).add(1, 'days');
            thunkAPI.dispatch(setActiveDay(updatedActiveDay));
          } else {
            thunkAPI.dispatch(endEditingWhereabouts());
          }
        } else {
          if (currentDay < 5) {
            const updatedActiveDay = moment(activeDay).add(1, 'days');
            thunkAPI.dispatch(setActiveDay(updatedActiveDay));
          } else {
            thunkAPI.dispatch(endEditingWhereabouts());
          }
        }
        if (activePeriod === Period.PM) {
          thunkAPI.dispatch(setActivePeriod(Period.AllDay));
        }
      } else {
        thunkAPI.dispatch(setActivePeriod(Period.PM));
      }
    }

    // Update server with whereabouts
    if (updatedWhereabouts && originalWhereabouts) {
      const failedWhereabouts = updatedWhereabouts;
      updatedWhereabouts = await saveWhereaboutsDay(updatedWhereabouts, originalWhereabouts, isOwn, thunkAPI.dispatch,
        (error) => handleWhereaboutsError(error, failedWhereabouts, thunkAPI.dispatch));

      // Week-scope editing is started from the wall planner grid, which
      // renders from its own slice that dispatchWhereaboutsSaved doesn't
      // cover; refetch after each save so the grid shows the new value.
      if (editMovementsState.editScope === 'week') {
        thunkAPI.dispatch(refreshWallPlannerActivities());
      }
    }

    return {
      activeDateString: activeDay,
      updatedWhereabouts: updatedWhereabouts,
    }
  }
)

export const applyDailyFilter: any = createAsyncThunk(
  'editMovements/applyDailyFilter',
  async (filter: Parameters<typeof setDailyFilter>[0], thunkAPI) => {
    thunkAPI.dispatch(setDailyFilter(filter));
    const state = thunkAPI.getState() as AppState;
    if (state.editMovements.focusedUserId) {
      thunkAPI.dispatch(endEditingWhereabouts());
    }
  }
);

export const openRepeatWhereaboutsDialog: any = createAsyncThunk(
  'editMovements/openRepeatWhereaboutsDialog',
  async (params: any, thunkAPI) => {
    const appState = (thunkAPI.getState() as AppState);
    thunkAPI.dispatch(openDialogWithPayload({
      payload: {
        whereabouts:  [...appState.editMovements.editingWhereabouts],
        userId: appState.editMovements.focusedUserId,
      },
      activeDialog: DialogIdentifiers.RepeatScheduleDialog,
    }))
  }
)

const editMovementsSlice = createSlice({
  name: 'editMovements',
  initialState: initialEditMovementsState,
  reducers: {
    resetEditMovements: () => ({...initialEditMovementsState}),
    setActiveDay: (state, action) => ({...state, activeDay: action.payload}),
    setActivePeriod: (state, action) => ({...state, activePeriod: action.payload}),
    setUserRequiresParking: (state, action) => ({...state, userRequiresParking: action.payload}),
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateMovement.pending, (state, action) => {
      const date = action.meta.arg.activeDay || state.activeDay?.format(DATE_FORMAT);
      const whereabouts = state.editingWhereabouts.map(w => w.date === date ? {...w, isWhereaboutsSaving: true} : w);
      return {
        ...state,
        editingWhereabouts: whereabouts,
      }
    })
      .addCase(updateMovement.fulfilled, (state, action) => {
      const date = action.payload.activeDateString;
      const whereabouts = state.editingWhereabouts.map(w => (w.date === date) ? action.payload.updatedWhereabouts : w);
      return {
        ...state,
        editingWhereabouts: whereabouts,
      }
    })
      .addCase(startEditingWhereabouts.fulfilled, (state, action) => ({
      ...state,
      focusedUserId: action.payload.userId,
      focusedGroupId: action.payload.groupId,
      activeDay: action.payload.date,
      activePeriod: action.payload.period,
      editingWhereabouts: action.payload.editingWhereabouts,
      userRequiresParking: action.payload.userRequiresParking,
      editScope: action.payload.scope,
    }))
      .addCase(endEditingWhereabouts.fulfilled, (state, action) => ({
      ...state,
      focusedUserId: undefined,
      focusedGroupId: undefined,
      editScope: 'day' as EditScope,
    }));
  }
})

export default editMovementsSlice.reducer;
export const {
  resetEditMovements,
  setActiveDay,
  setActivePeriod,
  setUserRequiresParking,
} = editMovementsSlice.actions;

// Selectors
export const selectActiveDay = (state: AppState) => state.editMovements.activeDay;
export const selectActivePeriod = (state: AppState) => state.editMovements.activePeriod;
export const selectFocusedUserId = (state: AppState) => state.editMovements.focusedUserId;
export const selectFocusedGroupId = (state: AppState) => state.editMovements.focusedGroupId;

export const selectUserRequiredParking = (state: AppState) => state.editMovements.userRequiresParking;

export const selectEditingWhereabouts = (state: AppState) => state.editMovements.editingWhereabouts;
export const selectEditScope = (state: AppState) => state.editMovements.editScope;
